import { useCallback, useLayoutEffect, useState } from 'react';

import {
  type AmplitudeProps,
  getCommonAmplitudeProperties,
  getSourceEventParams,
  useAmplitude,
} from '@crehana/analytics';
import { useOrganizationAccessLevel } from '@crehana/compass.knowledgehub/hooks';
import { StatusSessionEnum } from '@crehana/compass.live-sessions';
import { useLiveSessions } from '@crehana/compass.live-sessions/queries';
import {
  useGetUserSummaryGamification,
  usePlayroomCoinEventValues,
} from '@crehana/compass.playroom';
import { cn } from '@crehana/compass.ui/v2';
import { useFeatureFlag } from '@crehana/feature-flags/react';
import { useMediaQuery } from '@crehana/react-hooks';
import {
  MainFooter,
  type StudyPlanActionName,
  useActionMetrics,
  useLearnLastActivityContext,
  useStudyPlan,
} from '@crehana/web';

import { useUserEnrollments } from '@/shared/components/MyCoursesWithRules/hooks';
import { useGetKnowledgeHubConfig } from '@/shared/hooks/use-get-knowledge-hub-config';
import { TrackSectionScroll } from '@/views/LearnDashboard/components';
import { useLearnDashboardContext } from '@/views/LearnDashboard/context';
import useHideHigherEducationHomeUserUI from '@/views/LearnDashboard/hooks/useHigherEducationHomeUserUI';
import { useGetTeamTracksMigration } from '@/views/LearnDashboard/hooks/useTeamTracks/use-get-team-tracks-migration';

import CatalogLayout from './components/CatalogLayout';
import LearningPlanLayout from './components/LearningPlanLayout';
import LastActivity from './components/last-activity';
import {
  AMPLITUDE_PLAYROOM_REWARD_BANNER_CTA_SOURCE,
  AMPLITUDE_PLAYROOM_REWARD_BANNER_CTA_SOURCE_DETAIL,
  AMPLITUDE_SCROLL_EVENT_NAME,
} from './constants';
import { useCollaboratorCatalogCourses } from './hooks/use-collaborator-catalog-courses';
import useActionsURL from './hooks/useActionsURL';
import useGetCanUseInduction from './hooks/useGetCanUseInduction';
import useGetCourseEnrollment from './hooks/useGetCourseEnrollment';
import { useNextLiveSession } from './hooks/useNextLiveSession';
import useGetCoursesWithoutTrack from './sections/CoursesThatDontBelongToTracks/hooks/useGetCoursesWithoutTrack';
import TrackCourseHandler from './sections/CoursesThatDontBelongToTracks/TrackCourseHandler';
import RewardsBanner from './sections/RewardsBanner/RewardsBanner';
import RewardsDialog from './sections/RewardsDialog';
import SkeletonStartSection from './sections/SkeletonStartSection';
import Tasks from './sections/Tasks/Tasks';
import type { StartSectionProps } from './types';

const StartSectionNew: React.FC<StartSectionProps> = ({ me, isDark }) => {
  const { logEvent } = useAmplitude();
  const { courseSlide, loadingCourseEnrollmentProgress } =
    useGetCourseEnrollment();
  const {
    organizationSlug,
    isPlayroomEnabled,
    isLoadingPlayroomActivated,
    redirectTo,
  } = useLearnDashboardContext();
  const isB2BOnlyPanel = Boolean(me.isB2BOnlyPanel);
  const organizationId = String(me.organization?.originalId);
  const { catalogCourses, loading: loadingCourses } =
    useCollaboratorCatalogCourses(Number(organizationId));
  const { matches: isMd } = useMediaQuery('md');
  const [isRewardsDialogOpen, setIsRewardsDialogOpen] =
    useState<boolean>(false);
  const { meRole } = useGetCanUseInduction();
  const { hasEnrollments } = useLearnLastActivityContext();
  const { getActionClickLearningPlan } = useActionsURL();
  const { hasCurrency } = useGetUserSummaryGamification({
    organizationId,
  });

  const { playroomLearningPathCoins, playroomCourseCoins } =
    usePlayroomCoinEventValues(organizationSlug as string, organizationId);
  const {
    hasKnowledgeHub: knowledgeHubActivated,
    hasCrehanaCatalog,
    hasThirdPartyCatalog,
    loading: loadingKnowledgeHubConfig,
  } = useGetKnowledgeHubConfig({
    organizationId,
  });

  const { coursesWithoutTrack, loading: loadingCourseEnrollment } =
    useGetCoursesWithoutTrack();

  const {
    teams,
    totalCountTeams,
    loading: loadingTracks,
  } = useGetTeamTracksMigration({
    organization_id: Number(organizationId),
    organization_slug: organizationSlug || '',
  });

  const { totalCount: totalEnrollmentsCount, loading: loadingEnrollments } =
    useUserEnrollments({
      // We only want to get total enrollments count (totalEnrollmentsCount) for the current user.
      // 'totalEnrollmentsCount' is independent of the 'first' variable.
      first: 1,
    });
  const { plan, loading: loadingPlan } = useStudyPlan();

  const { loading: loadingLiveSessionData, sessions } = useLiveSessions(
    Number(organizationId),
    StatusSessionEnum.NEXT,
  );

  const nextLiveSession = useNextLiveSession({
    sessions,
    loading: loadingLiveSessionData,
  });

  const { flagValue: shouldHideAppsColumn } = useFeatureFlag(
    'LEARNING_HIDE_BANNER_DOWNLOAD_APP',
  );
  const showMarketplaceUIFF = useHideHigherEducationHomeUserUI();
  const { isPureLMSOrganization, loading: isLoadingOrganizationAccessLevel } =
    useOrganizationAccessLevel({
      orgId: +organizationId,
    });
  const isMarketplaceUser =
    me?.organization?.myRole === 'high-education-member';
  const isMarketplaceUserAndUIFF = isMarketplaceUser && showMarketplaceUIFF;
  const shouldRenderCoursesThatDontBelongToTracks = !isMarketplaceUserAndUIFF;
  const shouldRenderKnowledgeHubCatalog =
    !isB2BOnlyPanel && !isMarketplaceUserAndUIFF && knowledgeHubActivated;

  const COINS_COURSE: number | undefined =
    playroomCourseCoins !== 0 ? playroomCourseCoins : undefined;

  const onActionClickLearningPlan = useCallback(
    (
      actionName: StudyPlanActionName,
      amplitude?: Partial<AmplitudeProps>,
    ): void => {
      const urlWithSource = getActionClickLearningPlan(actionName, amplitude);

      redirectTo(urlWithSource);
    },
    [getActionClickLearningPlan, redirectTo],
  );

  const renderTrackCourseHandler = () => {
    return (
      <TrackCourseHandler
        containerType="dashboard"
        teams={teams}
        totalCountTeams={totalCountTeams}
        courses={coursesWithoutTrack}
        liveSessionData={sessions}
        playroomCourseCoins={COINS_COURSE}
        playroomTrackCoins={
          playroomLearningPathCoins !== 0
            ? playroomLearningPathCoins
            : undefined
        }
        knowledgeHubActivated={knowledgeHubActivated && !isPureLMSOrganization}
      >
        <TrackSectionScroll
          amplitudeEventName={AMPLITUDE_SCROLL_EVENT_NAME}
          amplitudeSource="Assigned Courses Section"
        />
      </TrackCourseHandler>
    );
  };

  const { loading: loadingMetrics, actionMetricProps } = useActionMetrics({
    onActionClick: onActionClickLearningPlan,
  });

  const shouldRenderLoading =
    loadingCourses ||
    loadingPlan ||
    loadingMetrics ||
    loadingCourseEnrollment ||
    loadingTracks ||
    loadingLiveSessionData ||
    loadingCourseEnrollmentProgress ||
    loadingKnowledgeHubConfig ||
    isLoadingOrganizationAccessLevel;

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentionally managed
  useLayoutEffect(() => {
    const isReadyToSentAmplitudeEventRender =
      !isLoadingPlayroomActivated && !loadingTracks && !loadingEnrollments;

    if (isReadyToSentAmplitudeEventRender) {
      const amplitudeParameters: Readonly<Record<string, string | number>> = {
        ...getSourceEventParams(),
        ...getCommonAmplitudeProperties(me, isPlayroomEnabled),
        'Learning Path Count': teams.length,
        'Courses Enrolled': totalEnrollmentsCount > 0 ? 'Yes' : 'No',
        'Courses Enrolled Count': totalEnrollmentsCount,
        'Quiz Certification Available': 'Yes',
        'Tracks Enrolled': teams.find(items => {
          // biome-ignore lint/style/useBlockStatements: concise single-line form preferred here
          if (
            items.coursesSummary?.inProgressCourses &&
            items.coursesSummary?.inProgressCourses > 0
          )
            return items;
          return undefined;
        })
          ? 'Yes'
          : 'No',
        'Study Plan Defined': plan ? 'Yes' : 'No',
      };

      logEvent({
        event: 'Render HomeUser Start',
        data: amplitudeParameters,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoadingPlayroomActivated,
    isPlayroomEnabled,
    loadingEnrollments,
    loadingTracks,
    teams.length,
    totalEnrollmentsCount,
    me,
  ]);

  if (shouldRenderLoading) {
    return <SkeletonStartSection />;
  }

  return (
    <section className="w-full h-full">
      {!isMarketplaceUserAndUIFF && (
        <div className="tw-overflow-hidden">
          <RewardsBanner
            isDark={isDark}
            isPlayroomEnabled={isPlayroomEnabled}
            hasCurrency={hasCurrency}
            amplitudeSource={AMPLITUDE_PLAYROOM_REWARD_BANNER_CTA_SOURCE}
            amplitudeSourceDetail={
              AMPLITUDE_PLAYROOM_REWARD_BANNER_CTA_SOURCE_DETAIL
            }
          />
        </div>
      )}
      <LastActivity
        isMarketplaceUserAndUIFF={isMarketplaceUserAndUIFF}
        onActionClickLearningPlan={onActionClickLearningPlan}
        isDark={isDark}
        catalogCourses={catalogCourses}
        plans={plan}
        actionMetrics={actionMetricProps}
        isCourseAssigned={coursesWithoutTrack.length > 0}
        coinsCourse={COINS_COURSE}
        nextLiveSession={nextLiveSession}
        courseProgress={courseSlide}
        hasCrehanaCatalog={hasCrehanaCatalog}
        hasThirdPartyCatalog={hasThirdPartyCatalog}
      />

      <RewardsDialog
        open={isRewardsDialogOpen}
        onDismiss={() => setIsRewardsDialogOpen(false)}
      />

      {meRole === 'induction' ? (
        <Tasks
          slug={organizationSlug as string}
          userId={String(me.originalId)}
        />
      ) : (
        <>
          {isMarketplaceUserAndUIFF && !isMd && (
            <LearningPlanLayout
              onActionClickLearningPlan={onActionClickLearningPlan}
              isDark={isDark}
              isCourseAssigned={coursesWithoutTrack.length > 0}
            />
          )}

          {hasEnrollments ? (
            <>
              {shouldRenderCoursesThatDontBelongToTracks &&
                renderTrackCourseHandler()}
            </>
          ) : (
            <>
              {shouldRenderKnowledgeHubCatalog && (
                <CatalogLayout showMoreContent={!isPureLMSOrganization} />
              )}

              {shouldRenderCoursesThatDontBelongToTracks &&
                !isB2BOnlyPanel &&
                renderTrackCourseHandler()}
            </>
          )}
        </>
      )}

      <TrackSectionScroll
        amplitudeEventName={AMPLITUDE_SCROLL_EVENT_NAME}
        amplitudeSource="Footer Section"
      />
      <MainFooter
        isDark={isDark}
        ignoreBgTransparent
        className={cn(!isDark && 'bg-white')}
        containerType="dashboard"
        shouldHideAppsColumn={shouldHideAppsColumn}
        layouts={{
          shouldHideWeAreHiringLink: true,
          shouldHideSignUpLink: true,
          shouldHideResourceSection: true,
        }}
      />
    </section>
  );
};

export default StartSectionNew;
